/**
 * Lexer of the expression language (W1-10c core-expr; contract src/core/expr/types.ts, D§4.11, D§5.8).
 *
 * Tokens: numbers with an optional unit suffix, identifiers, quoted curve names, the operators
 * `+ - * / % ^` (`**` is accepted for `^`), parentheses, commas and the assignment operators
 * `= := += -= *= /=` (the parser only accepts them at the start).
 *
 * Numbers: `12`, `12.5`, `.5`, `5.`, `1e3`, `1.5e-3`. A unit is written right after the number (`45deg`, `50%`,
 * `500ms`, `3v`); the unambiguous units `px`, `deg`, `ms` and the degree sign may also follow after spaces
 * (`12 px`, `45 °`, as the inspector displays values). Letters after a number are always read as its unit, so
 * `2pi` is reported by the checker as "write 2 * pi" instead of silently multiplying.
 * `%` right after a number (no space) is the percent unit unless an operand follows (`50%`, `50% + 10`,
 * `max(50%, 20%)`); otherwise it is the remainder operator (`7%2`, `i % 2`, `7 % 2`, `7 % -2`). Two spellings
 * could be either, so they are errors that show both readings:
 *   - a sign between them with no spaces (`7%-2`, `50%+u`): `7% - 2` subtracts from a percentage, `7 % -2` is
 *     the remainder;
 *   - a space after the percent sign only (`50% u`): `50% * u` multiplies a percentage (an operator was
 *     probably forgotten), `50 % u` is the remainder.
 * After a space, `%` is the percent unit only where a remainder cannot follow, at the end or before `)` or `,`
 * (`50 %`, `max(50 %, 20 %)`); `Expr.plainNumber` accepts the same spellings.
 *
 * The degree sign is a unit too (`45°` = `45deg`).
 *
 * Identifiers: `[A-Za-z_][A-Za-z0-9_]*`, `π` (= pi), and `$name$` (RPE complex-move style `$t$`).
 *
 * Input from other keyboards is normalised character by character (positions stay the same): full-width ASCII
 * (Chinese and Japanese IMEs: `（`, `）`, `，`, `＋`, digits), the ideographic comma and full stop, the minus sign
 * and dashes, `×`, `·`, `÷`, curly quotes, non-breaking and other Unicode spaces (number formats write "50 %" with
 * a narrow no-break space).
 */

import { EXPR_MSG, fail, type Span } from './errors';
import type { AssignOp } from './types';

export type BinOpChar = '+' | '-' | '*' | '/' | '%' | '^';

export interface NumToken extends Span {
  readonly kind: 'num';
  readonly value: number;
  /** Unit suffix as written (case kept), or null. */
  readonly unit: string | null;
  /** Start of the unit suffix (== `to` when there is none). */
  readonly unitFrom: number;
  /** End of the number itself (before the unit). */
  readonly numTo: number;
}

export interface IdentToken extends Span {
  readonly kind: 'ident';
  /** Name without `$` delimiters; `π` stays `π` (the checker maps it to pi). */
  readonly name: string;
}

export interface StrToken extends Span {
  readonly kind: 'str';
  readonly value: string;
}

export interface OpToken extends Span {
  readonly kind: 'op';
  readonly op: BinOpChar;
}

export interface AssignToken extends Span {
  readonly kind: 'assign';
  readonly op: AssignOp;
}

export interface PunctToken extends Span {
  readonly kind: '(' | ')' | ',';
}

export type Token = NumToken | IdentToken | StrToken | OpToken | AssignToken | PunctToken;

/** Single-character replacements applied before lexing (index-preserving). */
const CHAR_MAP: ReadonlyMap<number, string> = new Map<number, string>([
  [0x00a0, ' '], // no-break space
  [0x3000, ' '], // ideographic space
  [0x202f, ' '], // narrow no-break space (number formats put it before %: "50 %")
  [0x2007, ' '], // figure space
  [0x2008, ' '], // punctuation space
  [0x2009, ' '], // thin space
  [0x200a, ' '], // hair space
  [0x2002, ' '], // en space
  [0x2003, ' '], // em space
  [0x2004, ' '],
  [0x2005, ' '],
  [0x2006, ' '],
  [0x2000, ' '],
  [0x2001, ' '],
  [0x205f, ' '], // medium mathematical space
  [0xfeff, ' '], // zero width no-break space
  [0x2212, '-'], // minus sign
  [0x2010, '-'],
  [0x2011, '-'],
  [0x2012, '-'],
  [0x2013, '-'], // en dash
  [0x2014, '-'], // em dash
  [0x2015, '-'],
  [0x00d7, '*'], // multiplication sign
  [0x00b7, '*'], // middle dot
  [0x22c5, '*'], // dot operator
  [0x2219, '*'], // bullet operator
  [0x00f7, '/'], // division sign
  [0x2215, '/'], // division slash
  [0x3001, ','], // ideographic comma
  [0x3002, '.'], // ideographic full stop
  [0x2018, "'"],
  [0x2019, "'"],
  [0x201c, '"'],
  [0x201d, '"'],
]);

/** Maps alternative characters to their ASCII equivalents; the result has the same length as `src`. */
export function normalizeChars(src: string): string {
  let out = '';
  for (let i = 0; i < src.length; i++) {
    const code = src.charCodeAt(i);
    if (code >= 0xff01 && code <= 0xff5e) out += String.fromCharCode(code - 0xfee0);
    else out += CHAR_MAP.get(code) ?? src.charAt(i);
  }
  return out;
}

export const PI_CHAR = 'π';
/** `45°` is the same as `45deg`. */
export const DEGREE_CHAR = '°';

function isDigit(c: string | undefined): boolean {
  return c !== undefined && c >= '0' && c <= '9';
}

function isIdentStart(c: string | undefined): boolean {
  return c !== undefined && ((c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_');
}

function isIdentPart(c: string | undefined): boolean {
  return isIdentStart(c) || isDigit(c);
}

function isSpace(c: string | undefined): boolean {
  return c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === '\f' || c === '\v';
}

function isQuote(c: string | undefined): boolean {
  return c === '"' || c === "'";
}

/** True when the text at `k` (after spaces) starts an operand: a number, a name, a quote or "(". */
function operandFollows(s: string, k: number): boolean {
  let j = k;
  while (isSpace(s[j])) j++;
  return operandAt(s, j);
}

/** True when the text at exactly `j` starts an operand. */
function operandAt(s: string, j: number): boolean {
  const c = s[j];
  return (
    isDigit(c) ||
    (c === '.' && isDigit(s[j + 1])) ||
    isIdentStart(c) ||
    c === PI_CHAR ||
    c === '$' ||
    c === '(' ||
    isQuote(c)
  );
}

const SPACED_UNIT_RE = /^(px|deg|ms)(?![A-Za-z0-9_])/i;

/** Splits `src` into tokens. Throws ExprFailure (unexpected character, unclosed quote, bad number). */
export function lex(src: string): Token[] {
  const s = normalizeChars(src);
  const tokens: Token[] = [];
  let i = 0;
  while (i < s.length) {
    const c = s.charAt(i);
    if (isSpace(c)) {
      i++;
      continue;
    }
    if (isDigit(c) || (c === '.' && isDigit(s[i + 1]))) {
      const tok = lexNumber(s, i);
      tokens.push(tok);
      i = tok.to;
      continue;
    }
    if (isIdentStart(c)) {
      let j = i + 1;
      while (isIdentPart(s[j])) j++;
      tokens.push({ kind: 'ident', name: s.slice(i, j), from: i, to: j });
      i = j;
      continue;
    }
    if (c === PI_CHAR) {
      tokens.push({ kind: 'ident', name: PI_CHAR, from: i, to: i + 1 });
      i++;
      continue;
    }
    if (c === '$') {
      let j = i + 1;
      if (!isIdentStart(s[j])) fail(EXPR_MSG.badDollar, { from: i, to: i + 1 });
      while (isIdentPart(s[j])) j++;
      if (s[j] !== '$') fail(EXPR_MSG.badDollar, { from: i, to: j });
      tokens.push({ kind: 'ident', name: s.slice(i + 1, j), from: i, to: j + 1 });
      i = j + 1;
      continue;
    }
    if (isQuote(c)) {
      const close = s.indexOf(c, i + 1);
      if (close < 0) fail(EXPR_MSG.unclosedString, { from: i, to: s.length });
      tokens.push({ kind: 'str', value: s.slice(i + 1, close), from: i, to: close + 1 });
      i = close + 1;
      continue;
    }
    const next = s[i + 1];
    switch (c) {
      case '+':
      case '-':
      case '/': {
        const op: '+' | '-' | '/' = c === '+' ? '+' : c === '-' ? '-' : '/';
        if (next === '=') {
          tokens.push({ kind: 'assign', op: op === '+' ? '+=' : op === '-' ? '-=' : '/=', from: i, to: i + 2 });
          i += 2;
        } else {
          tokens.push({ kind: 'op', op, from: i, to: i + 1 });
          i++;
        }
        continue;
      }
      case '*':
        if (next === '=') {
          tokens.push({ kind: 'assign', op: '*=', from: i, to: i + 2 });
          i += 2;
        } else if (next === '*') {
          tokens.push({ kind: 'op', op: '^', from: i, to: i + 2 });
          i += 2;
        } else {
          tokens.push({ kind: 'op', op: '*', from: i, to: i + 1 });
          i++;
        }
        continue;
      case '%':
      case '^':
        tokens.push({ kind: 'op', op: c === '%' ? '%' : '^', from: i, to: i + 1 });
        i++;
        continue;
      case '=':
        tokens.push({ kind: 'assign', op: '=', from: i, to: i + 1 });
        i++;
        continue;
      case ':':
        if (next === '=') {
          tokens.push({ kind: 'assign', op: '=', from: i, to: i + 2 });
          i += 2;
          continue;
        }
        break;
      case '(':
      case ')':
      case ',':
        tokens.push({ kind: c === '(' ? '(' : c === ')' ? ')' : ',', from: i, to: i + 1 });
        i++;
        continue;
      default:
        break;
    }
    const cp = src.codePointAt(i) ?? 0;
    const width = cp > 0xffff ? 2 : 1;
    fail(EXPR_MSG.unexpectedChar, { from: i, to: i + width }, { char: src.slice(i, i + width) });
  }
  return tokens;
}

function lexNumber(s: string, start: number): NumToken {
  let j = start;
  let sawDot = false;
  while (isDigit(s[j])) j++;
  if (s[j] === '.') {
    sawDot = true;
    j++;
    while (isDigit(s[j])) j++;
  }
  if (s[j] === 'e' || s[j] === 'E') {
    let k = j + 1;
    if (s[k] === '+' || s[k] === '-') k++;
    if (isDigit(s[k])) {
      while (isDigit(s[k])) k++;
      j = k;
    }
  }
  // "1.2.3" or "1..2": a second decimal point directly after a number.
  if (s[j] === '.' && (sawDot || isDigit(s[j + 1]) || s[j + 1] === '.')) {
    let k = j;
    while (isDigit(s[k]) || s[k] === '.') k++;
    fail(EXPR_MSG.badNumber, { from: start, to: k }, { text: s.slice(start, k) });
  }
  const text = s.slice(start, j);
  const value = Number(text);
  if (!Number.isFinite(value)) fail(EXPR_MSG.numberTooLarge, { from: start, to: j });
  const numTo = j;
  // Unit suffix.
  if (isIdentStart(s[j])) {
    let k = j + 1;
    while (isIdentPart(s[k])) k++;
    return { kind: 'num', value, unit: s.slice(j, k), unitFrom: j, numTo, from: start, to: k };
  }
  if (s[j] === PI_CHAR || s[j] === DEGREE_CHAR) {
    return { kind: 'num', value, unit: s.charAt(j), unitFrom: j, numTo, from: start, to: j + 1 };
  }
  if (s[j] === '%') {
    const sign = s[j + 1];
    if ((sign === '-' || sign === '+') && operandAt(s, j + 2)) ambiguousPercent(s, start, j, sign);
    if (!operandFollows(s, j + 1)) return { kind: 'num', value, unit: '%', unitFrom: j, numTo, from: start, to: j + 1 };
    if (isSpace(s[j + 1])) percentThenSpace(s, start, j);
  }
  let k = j;
  while (isSpace(s[k])) k++;
  if (k > j) {
    const m = SPACED_UNIT_RE.exec(s.slice(k));
    if (m !== null) {
      const unit = m[1] ?? '';
      return { kind: 'num', value, unit, unitFrom: k, numTo, from: start, to: k + unit.length };
    }
    if (s[k] === DEGREE_CHAR || (s[k] === '%' && percentEnds(s, k + 1))) {
      return { kind: 'num', value, unit: s.charAt(k), unitFrom: k, numTo, from: start, to: k + 1 };
    }
  }
  return { kind: 'num', value, unit: null, unitFrom: j, numTo, from: start, to: j };
}

/** True when nothing a remainder needs follows `k`: the end of the input, `)` or `,` (after spaces). */
function percentEnds(s: string, k: number): boolean {
  let j = k;
  while (isSpace(s[j])) j++;
  const c = s[j];
  return c === undefined || c === ')' || c === ',';
}

/** The operand at `k` as written when it is a plain number or name, "..." otherwise, and where it ends. */
function operandWord(s: string, k: number): { readonly rest: string; readonly to: number } {
  const word = /^[A-Za-z0-9_.]+/.exec(s.slice(k))?.[0];
  return word === undefined ? { rest: '...', to: k } : { rest: word, to: k + word.length };
}

/** `7%-2`: a percentage minus 2, or the remainder of 7 divided by -2? Reports both spellings. */
function ambiguousPercent(s: string, start: number, pct: number, sign: string): never {
  const { rest, to } = operandWord(s, pct + 2);
  return fail(EXPR_MSG.percentOrRemainder, { from: start, to }, { num: s.slice(start, pct), sign, rest });
}

/** `50% u`: a percentage with a forgotten operator, or the remainder of 50 divided by u? Reports both. */
function percentThenSpace(s: string, start: number, pct: number): never {
  let k = pct + 1;
  while (isSpace(s[k])) k++;
  const { rest, to } = operandWord(s, k);
  return fail(EXPR_MSG.percentSpace, { from: start, to: Math.max(to, k + 1) }, { num: s.slice(start, pct), rest });
}

/** True when the token can start an operand (used for "missing operator" diagnostics). */
export function startsOperand(t: Token): boolean {
  return t.kind === 'num' || t.kind === 'ident' || t.kind === 'str' || t.kind === '(';
}
